/**
 * Time suggestions hook for autocomplete functionality
 * Generates smart time suggestions based on input, constraints, and context
 */

import { useMemo } from 'react'
import { Temporal } from '@js-temporal/polyfill'

import {
  formatTime,
  isTimeInRange,
  roundTimeToStep,
  getCurrentTime,
} from '../utils/temporalHelpers'
import { useTimeParser } from './useTimeParser'
import type {
  TimeSuggestion,
  SmartTimeInputProps,
  TimeOverride,
} from '../types'

export interface UseTimeSuggestionsOptions {
  /** Input text to generate suggestions for */
  input: string
  /** Hour cycle for displaying time (12 or 24 hour) */
  hourCycle?: SmartTimeInputProps['hourCycle']
  /** Step interval in minutes */
  step?: number
  /** Minimum allowed time */
  min?: Temporal.PlainTime
  /** Maximum allowed time */
  max?: Temporal.PlainTime
  /** Current time value for context */
  currentValue?: Temporal.PlainTime
  /** Timezone for context */
  timezone?: string
  /** Time overrides for custom behavior/styling */
  timeOverrides?: TimeOverride[]
}

export interface UseTimeSuggestionsReturn {
  /** Array of time suggestions */
  suggestions: TimeSuggestion[]
  /** Whether suggestions are loading/processing */
  loading: boolean
  /** Index of suggestion that exactly matches current input (for highlighting) */
  matchIndex: number
  /** Index of nearest suggestion for scrolling purposes */
  scrollIndex: number
}

/**
 * Custom hook for generating smart time suggestions
 */
export function useTimeSuggestions(
  options: UseTimeSuggestionsOptions,
): UseTimeSuggestionsReturn {
  const {
    input,
    hourCycle = 12,
    step = 15,
    min,
    max,
    currentValue,
    timezone,
    timeOverrides = [],
  } = options

  const { parseTime } = useTimeParser({
    mode: 'smart',
    min,
    max,
    timezone,
  })

  const format: '12' | '24' = hourCycle === 12 ? '12' : '24'

  // Create override lookup map for O(1) access
  const overridesMap = useMemo(() => {
    const map = new Map<string, TimeOverride>()
    timeOverrides.forEach((override) => {
      const key = `${override.time.hour}:${override.time.minute}`
      map.set(key, override)
    })
    return map
  }, [timeOverrides])

  const allSuggestions = useMemo(() => {
    const suggestions: TimeSuggestion[] = []
    const totalMinutesInDay = 24 * 60

    if (step < 1) {
      return suggestions
    }

    for (let i = 0; i < totalMinutesInDay; i += step) {
      const hour = Math.floor(i / 60)
      const minute = i % 60
      const currentTime = new Temporal.PlainTime(hour, minute)

      if (isTimeInRange(currentTime, min, max)) {
        const key = `${hour}:${minute}`
        const override = overridesMap.get(key)

        // Skip times marked for removal
        if (override?.remove) {
          continue
        }

        suggestions.push({
          time: currentTime,
          displayText: formatTime(currentTime, format),
          confidence: 1.0,
          source: 'generated',
          disabled: override?.disabled,
          variant: override?.variant,
          tooltipText: override?.tooltipText,
        })
      }
    }
    return suggestions
  }, [step, min, max, format, overridesMap])

  const matchIndex = useMemo(() => {
    const trimmedInput = input.trim()
    let targetTime: Temporal.PlainTime | null = null

    if (trimmedInput) {
      const parseResult = parseTime(trimmedInput)
      if (parseResult.success && parseResult.time) {
        // Only use the parsed time if it's already aligned with the step
        // Don't round it - we want exact matches only for highlighting
        const parsedTime = parseResult.time
        const roundedTime = roundTimeToStep(parsedTime, step)

        // Only set targetTime if the parsed time exactly matches the step interval
        if (parsedTime.equals(roundedTime)) {
          targetTime = parsedTime
        }
      }
    } else if (currentValue) {
      targetTime = roundTimeToStep(currentValue, step)
    } else {
      // Default to current time of day if no input and no value
      targetTime = roundTimeToStep(getCurrentTime(timezone), step)
    }

    if (targetTime) {
      const index = allSuggestions.findIndex((suggestion) =>
        suggestion.time.equals(targetTime),
      )
      return index
    }

    return -1
  }, [input, parseTime, allSuggestions, currentValue, step, timezone])

  // Calculate scrollIndex - finds nearest suggestion for scrolling (even if not exact match)
  const scrollIndex = useMemo(() => {
    const trimmedInput = input.trim()
    let targetTime: Temporal.PlainTime | null = null

    if (trimmedInput) {
      const parseResult = parseTime(trimmedInput)
      if (parseResult.success && parseResult.time) {
        // Round to step to find nearest suggestion for scrolling
        targetTime = roundTimeToStep(parseResult.time, step)
      }
    } else if (currentValue) {
      targetTime = roundTimeToStep(currentValue, step)
    } else {
      // Default to current time of day if no input and no value
      targetTime = roundTimeToStep(getCurrentTime(timezone), step)
    }

    if (targetTime) {
      const index = allSuggestions.findIndex((suggestion) =>
        suggestion.time.equals(targetTime),
      )
      return index
    }

    return -1
  }, [input, parseTime, allSuggestions, currentValue, step, timezone])

  return {
    suggestions: allSuggestions,
    loading: false,
    matchIndex,
    scrollIndex,
  }
}
